import { Component, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { KeyValuePair, Namespace, SessionToolCallData } from '@vendasta/protos/ai_assistants/v1alpha1';
import { getSessionEvent$ } from '@vendasta/protos/ai_assistants/v1alpha1/session-service';
import { firstValueFrom } from 'rxjs';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { Clipboard } from '@angular/cdk/clipboard';
import { AlertComponent } from '@vendasta/galaxy/alert/src/alert.component';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

export interface FunctionCallModalData {
  sessionEventId: string;
  conversationId: string;
  namespaceType: string;
  namespaceID: string;
  title: string;
  description?: string;
  inlineContent?: string;
  inlineArguments?: string;
}

@Component({
  selector: 'inbox-function-call-modal',
  templateUrl: './function-call-modal.component.html',
  styleUrl: './function-call-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MatDialogModule,
    TranslateModule,
    CommonModule,
    MatButtonModule,
    MatIconModule,
    AlertComponent,
    MatProgressSpinnerModule,
  ],
})
export class FunctionCallModalComponent {
  loading = true;
  loadError = false;
  functionCompleteURL = '';
  functionRequestBodyArgs: any = null;
  functionHeaders: Record<string, string> = {};
  content: any = null;
  contentTruncated = false;
  contentOriginalSizeKb = 0;

  public readonly dialogRef = inject(MatDialogRef<FunctionCallModalComponent>);
  public readonly data = inject<FunctionCallModalData>(MAT_DIALOG_DATA);
  private readonly clipboard = inject(Clipboard);
  private readonly toolCall = signal<SessionToolCallData | undefined>(undefined);

  constructor() {
    this.loadSessionEvent();
  }

  get hasHeaders(): boolean {
    return Object.entries(this.functionHeaders)?.length > 0;
  }

  private async loadSessionEvent(): Promise<void> {
    if (this.data.sessionEventId && this.data.conversationId) {
      await this.loadFromSessionApi();
    } else if (this.data.inlineContent || this.data.inlineArguments) {
      this.loadFromInlineData();
    } else {
      this.loadError = true;
    }
    this.loading = false;
  }

  private loadFromInlineData(): void {
    try {
      this.content = JSON.parse(this.data.inlineContent || '{}');
    } catch {
      this.content = this.data.inlineContent;
    }
    try {
      const args = JSON.parse(this.data.inlineArguments || '{}');
      this.functionRequestBodyArgs = Object.keys(args).length > 0 ? args : null;
    } catch {
      this.functionRequestBodyArgs = null;
    }
  }

  private async loadFromSessionApi(): Promise<void> {
    const namespace = namespaceFromTypeAndId(this.data.namespaceType, this.data.namespaceID);

    try {
      const response = await firstValueFrom(
        getSessionEvent$({
          sessionId: this.data.conversationId,
          eventId: this.data.sessionEventId,
          namespace,
        }),
      );

      const toolCall = response.event?.toolCall;
      this.toolCall.set(toolCall);
      if (!toolCall) {
        this.loadError = true;
        return;
      }

      if (toolCall.truncated) {
        this.contentTruncated = true;
        this.contentOriginalSizeKb = Math.floor((toolCall.originalSize || 0) / 1024);
      }

      try {
        this.content = JSON.parse(toolCall.response || '{}');
      } catch {
        this.content = toolCall.response;
      }
      this.functionCompleteURL = toolCall.url;
      toolCall.headers?.forEach((header: KeyValuePair) => {
        this.functionHeaders[header.key] = header.value;
      });
      const args = JSON.parse(toolCall.arguments || '{}');
      this.functionRequestBodyArgs = Object.keys(args).length > 0 ? args : null;
    } catch (e) {
      console.error('Failed to load session event:', e);
      this.loadError = true;
    }
  }

  copyCurlCommand(): void {
    const toolCall = this.toolCall();
    if (!this.functionCompleteURL || !toolCall) return;

    const hasBody = this.functionRequestBodyArgs && Object.keys(this.functionRequestBodyArgs).length > 0;
    let curlCommand = `curl -X ${toolCall.methodType} `;

    const headers = { ...this.functionHeaders };
    if (hasBody && !headers['Content-Type']) {
      headers['Content-Type'] = 'application/json';
    }
    if (Object.entries(headers)?.length > 0) {
      Object.entries(headers).forEach(([key, value]) => {
        curlCommand += `-H '${key}: ${value}' `;
      });
    }

    if (hasBody) {
      curlCommand += `-d '${JSON.stringify(this.functionRequestBodyArgs)}' `;
    }

    curlCommand += `'${this.functionCompleteURL}'`;

    this.clipboard.copy(curlCommand);
  }
}

function namespaceFromTypeAndId(namespaceType: string, namespaceId: string): Namespace {
  if (!namespaceType || !namespaceId) return { globalNamespace: {} } as Namespace;
  switch (namespaceType.toUpperCase()) {
    case 'NAMESPACE_TYPE_ACCOUNT_GROUP':
    case 'ACCOUNT_GROUP':
    case 'ACCOUNTGROUP':
      return { accountGroupNamespace: { accountGroupId: namespaceId } } as Namespace;
    case 'NAMESPACE_TYPE_PARTNER':
    case 'PARTNER':
      return { partnerNamespace: { partnerId: namespaceId } } as Namespace;
    case 'NAMESPACE_TYPE_SYSTEM':
    case 'SYSTEM':
      return { systemNamespace: {} } as Namespace;
    default:
      return { globalNamespace: {} } as Namespace;
  }
}
